'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  HOURS_BOOKING,
  teamMember,
  YEAR_ACTIVITIES,
  YEAR_ACTIVITY_PCT,
  YEAR_BOARD,
  YEAR_FLIP_BEATS,
  YEAR_TOTALS,
  type PlanningCell,
  type PlanningState,
} from '../demo-data'
import { legalFormLabel, useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AppSubHeader, HeaderStat, PlanningPill } from './app-screen'
import styles from './jaarplanning-visual.module.css'
import { TypingDots } from './typing-dots'
import { useScenario } from './use-scenario'

type Tone = PlanningState
type Cell = PlanningCell

// The 9-row planbord body comes straight from the canonical year planning, so the clients, their
// legal forms and their cell states match every other visual. Cell flips (and the "selected" cell
// below) are keyed to specific beats of the 21-beat loop — see `YEAR_FLIP_BEATS`.
const ROWS = YEAR_BOARD.map((row) => {
  const { name, form, parent } = client(row.nr)
  return { name, tag: form, indent: Boolean(parent), cells: row.cells }
})

// The row/column of the cell the cursor clicks (Van Dijk Vastgoed's Jaarrekening cell — "loopt"
// until it finishes at beat 11). It gets the blue "click" frame from the click beat onward.
const SELECTED_ROW = 1
const SELECTED_COL = 0
const SELECTED_FROM_BEAT = 2 // the cursor's click beat — the ring lands the moment it clicks

const TOTAL_BEATS = 21
const REST_BEAT = 14 // SSR / reduced-motion / not-yet-playing snapshot — a filled, mid-flow board
const RESET_BEAT = 20 // final beat of the loop: cross-fades back toward the sparse start

// The 3 cells that flip to "klaar" mid-loop — each completes one planning item, so the done
// counter ticks by one and (for the Jaarrekening column) nudges that column's progress bar.
const KLAAR_FLIP_BEATS = YEAR_FLIP_BEATS
const DONE_AT_START = YEAR_TOTALS.doneAtStart
const DONE_PER_FLIP = 1
const TOTAL_TAKEN = YEAR_TOTALS.total

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive board state. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

function toneAt(cell: Cell, beat: number): Tone {
  return typeof cell === 'string' ? cell : beat >= cell.at ? cell.to : cell.from
}

function flipCount(beat: number): number {
  return KLAAR_FLIP_BEATS.filter((flipBeat) => beat >= flipBeat).length
}

function doneTaken(beat: number): number {
  return DONE_AT_START + flipCount(beat) * DONE_PER_FLIP
}

function jaarrekeningPct(beat: number): number {
  // Grows 57% -> 58% -> 59% as the column's cells flip, +1 per flip capped at +2 (matches the
  // fixed integer steps in Hero Jaarplanning — never the intermediate 58% a ×2/3 curve would show).
  return YEAR_ACTIVITY_PCT.jaarrekening + Math.min(flipCount(beat), 2)
}

const columns = (t: VisualCopy) =>
  YEAR_ACTIVITIES.map((activity) => ({
    label: t.yearActivity[activity.key].label,
    pct: activity.key === 'jaarrekening' ? jaarrekeningPct : () => YEAR_ACTIVITY_PCT[activity.key],
  }))

/** Renders one board cell through the shared planning pill (identical to the month boards). */
function Pill({
  tone,
  ringed,
  extra,
  cellRef,
}: {
  tone: Tone
  ringed?: boolean
  extra?: boolean
  cellRef?: RefObject<HTMLSpanElement | null>
}) {
  return (
    <PlanningPill
      size="hero"
      tone={tone}
      ringed={ringed}
      cellRef={cellRef}
      className={extra ? styles.colExtra : undefined}
    />
  )
}

type BeatWindow = readonly [from: number, to: number]

function withinWindow(beat: number, [from, to]: BeatWindow): boolean {
  return beat >= from && beat < to
}

const CHECKLIST_WINDOW: BeatWindow = [3, 8]
const CHAT_OUT_WINDOW: BeatWindow = [6, 12]
// One incoming bubble for the whole reply: it holds the typing dots, then swaps them for the reply
// text in place at REPLY_BEAT — same bubble, same position, so nothing pops in or jumps.
const CHAT_IN_WINDOW: BeatWindow = [8, 12]
const REPLY_BEAT = 10
const NOTIFICATION_WINDOW: BeatWindow = [12, 16]
const UREN_WINDOW: BeatWindow = [14, 19]

// Vertical padding the `.cardSlot` adds as a drop-shadow gutter (see the module CSS). Because the
// slot is border-box, this padding counts against max-height, so every maxHeight below adds it back.
const SHADOW_GUTTER_Y = 58

// The one narrative that ties the whole visual together: the client relationship manager hands the
// Van Dijk Vastgoed jaarrekening to the assistant accountant, who picks it up (cursor + checklist),
// replies in chat, finishes it (notification), and books her hours on it. Names, client and task
// all come from the shared demo data, so they match the other visuals.
const HANDOVER_FROM = { ...teamMember('SV'), time: '14:22' }
const ASSIGNEE = { ...teamMember(HOURS_BOOKING.member), time: HOURS_BOOKING.at }
const TASK_CLIENT = client(HOURS_BOOKING.clientNr)

/** The shared annual-accounts checklist, one step ticked every other beat. */
const checklistItems = (t: VisualCopy) =>
  t.content.annualAccountsChecklist.map((label, index) => ({ label, at: 3 + index * 2 }))

/** One slot in the floating stack: collapses to nothing when not in its beat window, fades + slides
 *  + scales in when it mounts (see the `.cardSlot` / `.cardInner` transition contract). */
function FloatCard({ visible, maxHeight, children }: { visible: boolean; maxHeight: number; children: ReactNode }) {
  return (
    <div
      className={clsx(styles.cardSlot, !visible && styles.cardSlotCollapsed)}
      style={{ maxHeight: visible ? maxHeight : 0 }}
    >
      <div className={clsx(styles.cardInner, visible && styles.cardInnerVisible)}>{children}</div>
    </div>
  )
}

type CursorPhase = 'hidden' | 'moving' | 'clicking' | 'settled'

function cursorPhase(beat: number | null): CursorPhase {
  if (beat === 1) return 'moving'
  if (beat === 2) return 'clicking'
  if (beat === 3) return 'settled'
  return 'hidden'
}

/** Measures the selected cell's centre relative to the board so the cursor can point exactly at it,
 *  in whatever layout the container query resolved to (re-measures on resize). */
function useCellCenter(boardRef: RefObject<HTMLDivElement | null>, cellRef: RefObject<HTMLSpanElement | null>) {
  const [center, setCenter] = useState<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const board = boardRef.current
    const cell = cellRef.current
    if (!board || !cell) return
    const measure = () => {
      const b = board.getBoundingClientRect()
      const c = cell.getBoundingClientRect()
      setCenter({ x: c.left - b.left + c.width / 2, y: c.top - b.top + c.height / 2 })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(board)
    return () => observer.disconnect()
  }, [boardRef, cellRef])
  return center
}

/**
 * Hero visual — a "Jaarplanning" planbord: a pill-status table for 9 clients across 4 workflow
 * columns, with a floating stack (checklist card -> chat bubbles + typing -> notification / hours
 * cards) that grows upward from the bottom-right, and a cursor animating one task getting picked up
 * and finished. Driven by `useScenario`'s 21-beat loop; `beat === null` (SSR, no-JS,
 * `prefers-reduced-motion: reduce`) renders the resting beat-14 snapshot: several cells flipped to
 * done, the notification + hours cards showing, no cursor.
 *
 * Responsive layout (2 columns + compact chrome under 640px) is CSS-only via @container queries on
 * `.board`, so the correct layout is painted on the first frame with no measurement flash.
 */
export function JaarplanningVisual({
  className,
  corners = 'hero',
}: {
  className?: string
  /**
   * `'all'` rounds every corner and keeps a full border (used on the Planning product hero, where
   * the panel sits inside a padded card). `'hero'` is the full-bleed home-hero treatment: the panel
   * is flush to its container's edges, so it rounds only the top corners (both on mobile, top-left
   * only on desktop) and drops the border on the bled edges (bottom always, plus right on desktop).
   */
  corners?: 'all' | 'hero'
}) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 880 })
  const cellRef = useRef<HTMLSpanElement | null>(null)
  const cursorCenter = useCellCenter(ref, cellRef)
  const effBeat = effectiveBeat(beat)
  const completionPct = (doneTaken(effBeat) / TOTAL_TAKEN) * 100
  const phase = cursorPhase(beat)
  const cursorActive = phase !== 'hidden'

  return (
    <AppUiVisual label={t.screen.jaarplanning.caption} className={className}>
      {/*
        `!rounded-*` uses Tailwind's `!important` modifier so it deterministically overrides
        `AppScreen`'s hardcoded `rounded-[10px]` regardless of Tailwind's generated-CSS source order
        (this project has no `tailwind-merge`) — see the corners prop doc above.
      */}
      <AppScreen
        className={clsx(
          styles.board,
          corners === 'hero' && '!rounded-t-lg !rounded-b-none !border-b-0 lg:!rounded-tr-none lg:!border-r-0',
        )}
      >
        <div ref={ref} data-visual="hero" className="flex h-full flex-col">
          <AppHeader
            size="hero"
            title={t.screen.jaarplanning.title}
            detail={t.period.closingBookYear}
            right={
              <div className={styles.stats}>
                <HeaderStat value={fmt.percent(completionPct)} label={t.word.complete} />
                <div className={styles.statSub}>
                  <b>{fmt.number(doneTaken(effBeat))}</b> / <b>{fmt.number(TOTAL_TAKEN)}</b> {t.word.tasks}
                </div>
              </div>
            }
          />

          <AppSubHeader
            size="hero"
            className={styles.tableGrid}
            rowLabel={t.word.client}
            columns={columns(t).map((col, colIndex) => ({
              label: (
                <>
                  {col.label} <b>{col.pct(effBeat)}%</b>
                </>
              ),
              pct: col.pct(effBeat),
              className: colIndex >= 2 ? styles.colExtra : undefined,
            }))}
          />

          <div className={styles.rowsBody}>
            {ROWS.map((row, rowIndex) => (
              <div key={row.name} className={clsx(styles.tableGrid, styles.row)}>
                <div className={styles.rowName} style={{ paddingLeft: row.indent ? 30 : 0 }}>
                  {row.indent && <span className={styles.elbow} aria-hidden="true" />}
                  <span className={styles.nameText}>{row.name}</span>
                  <span className={styles.rowTag}>{legalFormLabel(t, row.tag)}</span>
                </div>
                {row.cells.map((cell, colIndex) => {
                  const tone = toneAt(cell, effBeat)
                  const isSelected = rowIndex === SELECTED_ROW && colIndex === SELECTED_COL
                  const ringed = isSelected && effBeat >= SELECTED_FROM_BEAT
                  return (
                    <Pill
                      key={colIndex}
                      tone={tone}
                      ringed={ringed}
                      extra={colIndex >= 2}
                      cellRef={isSelected ? cellRef : undefined}
                    />
                  )
                })}
              </div>
            ))}
          </div>

          {/* Inside the visual's own root, not beside it. The stack is absolutely positioned against
              the board, so moving it in changes no layout — but it puts the chat bubbles under the
              element that carries `data-animating`, which is what starts the typing dots. Outside
              it, the assistant sat thinking with three motionless dots. */}
          <div className={styles.floatStack} aria-hidden="true">
            <FloatCard visible={withinWindow(effBeat, CHECKLIST_WINDOW)} maxHeight={180 + SHADOW_GUTTER_Y}>
              <div className={styles.floatCard}>
                <div className={styles.floatCardHeader}>
                  {t.word.checklist} · {t.yearActivity.jaarrekening.label}
                </div>
                <div className="mt-2 flex flex-col gap-2">
                  {checklistItems(t).map((item) => {
                    const checked = effBeat >= item.at
                    return (
                      <div key={item.label} className={styles.checklistRow}>
                        <span className={clsx(styles.checkbox, checked && styles.checkboxChecked)}>
                          {checked && '✓'}
                        </span>
                        <span className={clsx(styles.checklistLabel, checked && styles.checklistLabelDone)}>
                          {item.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </FloatCard>

            <FloatCard visible={withinWindow(effBeat, CHAT_OUT_WINDOW)} maxHeight={104 + SHADOW_GUTTER_Y}>
              <div className="flex items-center justify-end gap-2">
                <div className={styles.chatBubble}>
                  <div className={styles.chatBubbleHead}>
                    <span className={styles.chatName}>{HANDOVER_FROM.name}</span>
                    <span className={styles.chatTime}>{HANDOVER_FROM.time}</span>
                  </div>
                  {t.screen.jaarplanning.chatQuestion(t.yearActivity.jaarrekening.label, TASK_CLIENT.short)}{' '}
                  <b>@{ASSIGNEE.firstName}</b>
                </div>
                <span className={styles.avatar} style={{ background: HANDOVER_FROM.color }}>
                  {HANDOVER_FROM.initials}
                </span>
              </div>
            </FloatCard>

            <FloatCard visible={withinWindow(effBeat, CHAT_IN_WINDOW)} maxHeight={104 + SHADOW_GUTTER_Y}>
              <div className="flex items-center justify-start gap-2">
                <span className={styles.avatar} style={{ background: ASSIGNEE.color }}>
                  {ASSIGNEE.initials}
                </span>
                <div className={styles.chatBubbleIn}>
                  <div className={styles.chatBubbleHead}>
                    <span className={styles.chatName}>{ASSIGNEE.name}</span>
                    <span className={styles.chatTime}>{ASSIGNEE.time}</span>
                  </div>
                  {effBeat < REPLY_BEAT ? <TypingDots matchTextLine /> : t.screen.jaarplanning.chatReply}
                </div>
              </div>
            </FloatCard>

            <FloatCard visible={withinWindow(effBeat, NOTIFICATION_WINDOW)} maxHeight={94 + SHADOW_GUTTER_Y}>
              <div className={clsx(styles.floatCard, 'flex items-center gap-2')}>
                <span className={styles.notifyIcon}>✓</span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className={styles.floatCardName}>
                    {t.screen.jaarplanning.activityDone(t.yearActivity.jaarrekening.label)}
                  </div>
                  <div className={styles.floatCardMeta}>
                    {TASK_CLIENT.short} · {t.word.by} {ASSIGNEE.firstName}
                  </div>
                </div>
              </div>
            </FloatCard>

            <FloatCard visible={withinWindow(effBeat, UREN_WINDOW)} maxHeight={150 + SHADOW_GUTTER_Y}>
              <div className={styles.floatCard}>
                <div className={styles.floatCardHeader}>{t.screen.hours.title}</div>
                <div className="mt-2 flex items-center gap-2.5">
                  <span className={styles.avatar} style={{ background: ASSIGNEE.color }}>
                    {ASSIGNEE.initials}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className={styles.floatCardName}>{ASSIGNEE.name}</div>
                    <div className={styles.floatCardMeta}>
                      {t.yearActivity.jaarrekening.label} · {TASK_CLIENT.short}
                    </div>
                  </div>
                  <div className={styles.urenAmount}>
                    <span className={styles.urenValue}>{fmt.decimal(HOURS_BOOKING.hours, 2)}</span>
                    <span className={styles.urenUnit}>{t.word.hoursUnit}</span>
                  </div>
                </div>
                <div className={styles.urenBooked}>{t.screen.jaarplanning.booked}</div>
              </div>
            </FloatCard>
          </div>
        </div>

        <span
          aria-hidden="true"
          className={clsx(
            styles.cursor,
            cursorActive && styles.cursorVisible,
            phase === 'clicking' && styles.cursorClicking,
          )}
          style={cursorActive && cursorCenter ? { left: cursorCenter.x - 3, top: cursorCenter.y - 2 } : undefined}
        >
          <svg
            className={styles.cursorGlyph}
            viewBox="0 0 22 26"
            width="24"
            height="28"
            aria-hidden="true"
            style={{ filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.35))' }}
          >
            <path
              d="M4.5 1.5 L4.5 18.6 L8.9 14.9 L11.6 21.3 C11.8 21.8 12.4 22 12.9 21.8 L14.7 21 C15.2 20.8 15.4 20.2 15.2 19.7 L12.5 13.4 L18.3 12.9 C19 12.8 19.3 12 18.8 11.5 L6.2 0.8 C5.6 0.3 4.5 0.7 4.5 1.5 Z"
              fill="#14171c"
              stroke="#ffffff"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </AppScreen>
    </AppUiVisual>
  )
}
